'use client';

import Carousel from 'components/shared/Carousel/Carousel';
import CarouselContent from 'components/shared/Carousel/CarouselContent';
import CarouselPagination from 'components/shared/Carousel/CarouselPagination';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { TestimonialProps } from 'libs/contentful/props';
import { cn, getColorway, getYMargins } from 'utils/styles';
import TestimonialItem from './TestimonialItem';
import useCarousel from 'hooks/useCarousel';
import { AutoplayOptionsType } from 'embla-carousel-autoplay';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { useAutoplayPlugin } from 'hooks/useAutoplayPlugin';
import { TestimonialVariants } from './constants';
import { OverflowVisibleSettings } from 'types/constants';

const Testimonial = ({
  sys,
  variant,
  sectionName,
  sectionColor,
  testimonialItems,
  disclaimer,
  slideColor,
  margins,
}: TestimonialProps) => {
  const { current, setApi, scrollTo } = useCarousel();

  const autoPlayOptions: AutoplayOptionsType = {
    delay: 5000,
    stopOnMouseEnter: true,
    stopOnFocusIn: true,
    stopOnInteraction: false,
    rootNode: (emblaRoot: HTMLElement) => emblaRoot.parentElement,
  };

  const isV2 = variant === TestimonialVariants.V2;
  const autoplayPlugin = useAutoplayPlugin(autoPlayOptions);
  const plugins = isV2 || !autoplayPlugin ? [] : [autoplayPlugin];

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cn(
        getColorway(sectionColor),
        getYMargins(margins),
        variant !== TestimonialVariants.V2 && 'px-6 md:px-14',
        'overflow-hidden',
      )}>
      <Carousel setApi={setApi} plugins={plugins} opts={{ align: 'start', slidesToScroll: 1, containScroll: false }}>
        <CarouselContent
          className={cn(
            'content-wrapper rounded-lg',
            getColorway(slideColor),
            !isV2 &&
              cn(
                'px-[24px] py-[32px]', // mobile
                'sm:px-[48px]', // tablet
                'md:px-[56px] ml:px-[64px] lg:px-[96px] lg:py-[80px]', // desktop + WebTV
              ),
          )}
          overflowVisible={isV2 ? OverflowVisibleSettings.All : OverflowVisibleSettings.None}>
          <div
            className={cn('flex space-x-8', isV2 && 'items-start')}
            {...liveUpdateAttrs(sys?.id, 'testimonialItems')}>
            {testimonialItems.items.map((item, index) => (
              <TestimonialItem
                key={`testimonial-item-${index}`}
                {...item}
                copy={item.copy}
                media={item.media}
                variant={variant}
                cta={item.cta}
                aspectRatio={item.aspectRatio}
                index={index}
              />
            ))}
          </div>

          <div className="flex flex-col lg:flex-row gap-4 lg:items-center mt-8 lg:mt-12">
            {testimonialItems.items.length > 1 && (
              <CarouselPagination total={testimonialItems.items.length} current={current} onSelect={scrollTo} />
            )}
            {disclaimer && (
              <div
                className={`font-sans text-xs font-medium ${testimonialItems.items[current].media ? 'lg:ml-auto' : 'lg:ml-[11%]'}`}
                data-testid="testimonialDisclaimer"
                {...liveUpdateAttrs(sys?.id, 'disclaimer')}>
                <RichTextCopy copy={disclaimer} />
              </div>
            )}
          </div>
        </CarouselContent>
      </Carousel>
    </section>
  );
};
export default Testimonial;
